import config from 'src/config';
import { isWarehouseFlagEnabled } from 'src/utils/warehouseFlag';

const AUTH0_ROLES_KEY = config().auth0_roles_key;

// Auth0 stores app roles under a namespaced custom claim on the user object
// (configured per-tenant as auth0_roles_key). Shared so any page can gate a
// feature on role membership the same way App.tsx gates routes.
export const getUserRoles = (
  user: Record<string, unknown> | undefined,
): string[] => {
  const roles = user?.[AUTH0_ROLES_KEY];
  if (!Array.isArray(roles)) return [];
  return roles.filter((role): role is string => typeof role === 'string');
};

// Whether the current user may see admin-gated surfaces.
//
// The mocked-auth dev stack (`npm run mock:auth:stack`) skips Auth0 entirely,
// so there is no user and no roles — which silently hid every admin-gated
// section locally, with no error to explain the absence. App.tsx already
// treats CYPRESS_AUTH_MOCKED as "assume authorised" when it bypasses
// ProtectedRoute; role gates follow the same rule rather than staying dark.
// The flag defaults to false in webpack.config.js and is set only by the two
// mock scripts, so it can never grant admin in a deployed build.
export const hasAdminRole = (
  user: Record<string, unknown> | undefined,
): boolean =>
  process.env.CYPRESS_AUTH_MOCKED === 'true' ||
  getUserRoles(user).includes('admin');

// Putaway is open to admin, bp-admin and driver at every warehouse. Anyone
// else needs the selected warehouse in their deliveryServicePutawayApp
// allow-list.
export const hasPutawayAppAccess = (
  user: Record<string, unknown> | undefined,
  putawayFlag: unknown,
  selectedWarehouseId: string | undefined,
): boolean =>
  hasAdminRole(user) ||
  getUserRoles(user).some((role) => role === 'bp-admin' || role === 'driver') ||
  isWarehouseFlagEnabled(putawayFlag, selectedWarehouseId);

// Putaway is the only page here a driver can use; the rest need admin. So
// drivers land on Putaway and only see it in the menu. Admin wins when a user
// has both roles.
export const isPutawayOnlyUser = (
  user: Record<string, unknown> | undefined,
): boolean => !hasAdminRole(user) && getUserRoles(user).includes('driver');
